// MobileLPPortal.jsx — mobile adaptation of the desktop LP portal.
// Full-screen "page" over the mobile desktop with the standalone-page
// bottom-taskbar pattern (same classes as PageTaskbar): Start returns to
// the desktop (closes the portal), one sunken button names the window,
// and the tray keeps the social links and the clock.
import React, { useEffect, useState } from "react";
import LimitedPartnerPortal from "./LimitedPartnerPortal";
import OSIcon32 from "../icons/OSIcon32";

const TrayLink = ({ href, title, path }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    aria-label={title}
    style={{ display: "flex", alignItems: "center", color: "var(--chrome-outline)" }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d={path} />
    </svg>
  </a>
);

export default function MobileLPPortal({ onClose }) {
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const yy = "03"; // hardcoded year — part of the Y2K-era aesthetic
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      setCurrentTime(`${mm}/${dd}/${yy} ${timeStr}`);
    };
    updateClock();
    const interval = setInterval(updateClock, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "var(--gnf-bg)",
        zIndex: 9995,
        display: "flex",
        flexDirection: "column",
        fontFamily: "var(--font-body)",
      }}
    >
      {/* Portal content fills above the taskbar */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          padding: "10px",
          paddingBottom: "calc(var(--taskbar-h, 34px) + 10px)",
        }}
      >
        <LimitedPartnerPortal
          onOpenGNFWebsite={() => window.open("https://goodneighbor.fund", "_blank")}
        />
      </div>

      {/* Win98 taskbar — same classes as PageTaskbar; Start closes the portal */}
      <div className="win95-taskbar-98">
        <button
          type="button"
          className="win95-taskbar-98__start"
          onClick={onClose}
          aria-label="Start — back to the desktop"
        >
          <img src="/assets/gnf-logo.png" alt="" width="16" height="16" style={{ display: "block" }} />
          Start
        </button>

        <div className="win95-toolbar__sep" aria-hidden="true" />

        <button
          type="button"
          className="win95-taskbar-btn is-active"
          aria-current="page"
          style={{ width: "auto", minWidth: 0, flex: "0 1 auto" }}
        >
          <OSIcon32 kind="portal" size={16} style={{ flex: "none" }} />
          LP Portal
        </button>

        <div style={{ flex: 1 }} />

        <div className="win95-taskbar-98__tray">
          <TrayLink
            href="https://www.linkedin.com/company/good-neighbor-fund"
            title="LinkedIn"
            path="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
          />
          <TrayLink
            href="https://www.instagram.com/goodneighborfund/"
            title="Instagram"
            path="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
          />
          <TrayLink
            href="https://goodneighbors.substack.com"
            title="Newsletter"
            path="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
          />
          <div className="win95-taskbar-98__tray-divider" aria-hidden="true" />
          <span className="retro-clock">{currentTime}</span>
        </div>
      </div>
    </div>
  );
}
